/**
 * Determines face shape based on measurements and questionnaire answers
 * @param {Object} answers - All questionnaire answers
 * @returns {string} - The determined face shape
 */
export function determineFaceShape(answers) {
  // Extract measurements and answers
  const faceRatio = answers[1]?.faceRatio || 0;
  const jawShape = answers[2]; // 'square', 'gently-rounded', 'rounded', 'pointed'
  const widestPart = answers[3]; // 'Forehead', 'Cheekbones', 'Jaw', 'All close in size'

  // Check for oblong
  if (faceRatio >= 1.6) {
    return 'oblong';
  }

  // Check for square/circle range with pointed jaw exception
  if (faceRatio < 1.3) {
    // Exception: if jaw is pointed, treat as 1.3-1.6 range
    if (jawShape === 'pointed') {
      return determineShapeFor1_3To1_6Range(widestPart, jawShape);
    }

    // Normal square/circle determination
    if (jawShape === 'square') {
      return 'square';
    }
    if (jawShape === 'rounded' || jawShape === 'gently-rounded') {
      return 'round';
    }
  }

  // Check for 1.3-1.6 range (oval, square/oval hybrid, diamond, heart, triangle)
  if (faceRatio >= 1.3 && faceRatio < 1.6) {
    return determineShapeFor1_3To1_6Range(widestPart, jawShape);
  }

  // Fallback (shouldn't reach here with valid data)
  return 'unknown';
}

/**
 * Helper function to determine face shape for ratio between 1.3 and 1.6
 * @param {string} widestPart - The widest part of the face
 * @param {string} jawShape - The jaw shape
 * @returns {string} - The determined face shape
 */
function determineShapeFor1_3To1_6Range(widestPart, jawShape) {
  // Check widest part
  if (widestPart === 'Forehead') {
    return 'heart';
  }
  
  if (widestPart === 'Cheekbones') {
    return 'diamond';
  }
  
  if (widestPart === 'Jaw') {
    return 'triangle';
  }
  
  // All close in size - check jaw shape
  if (widestPart === 'All are close in size') {
    if (jawShape === 'square') {
      return 'square/oval hybrid';
    }
    // All non-square jaw shapes return oval
    return 'oval';
  }

  // Fallback
  return 'unknown';
}

/**
 * Gets a description for the determined face shape
 * @param {string} faceShape - The face shape
 * @returns {string} - Description of the face shape
 */
export function getFaceShapeDescription(faceShape) {
  const descriptions = {
    'oblong': 'Your face is longer than it is wide, with a relatively straight and equal width from forehead to jaw.',
    'square': 'Your face has a strong, angular jawline with similar width at the forehead and jaw.',
    'round': 'Your face has soft, curved lines with similar length and width, and a rounded jawline.',
    'oval': 'Your face is slightly longer than wide with gently rounded features and a narrower jaw than forehead.',
    'square/oval hybrid': 'Your face combines features of both square and oval shapes, with balanced proportions and a defined but not overly angular jaw.',
    'diamond': 'Your face is widest at the cheekbones, with a narrow forehead and jawline.',
    'heart': 'Your face is widest at the forehead and narrows down to a pointed or narrow chin.',
    'triangle': 'Your face is widest at the jaw and narrows toward the forehead.',
    'unknown': 'Unable to determine face shape with the provided measurements.'
  };

  return descriptions[faceShape] || descriptions['unknown'];
}
